import { useNavigate, useLocation } from 'react-router-dom';
import { AppShell, Content } from '../../components/ui/AppShell';
import { MiniLeagueCard } from '../../components/miniLeagues/LeagueCard';
import { ChevronLeft, ListOrdered, Plus } from '../../components/ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { useAuth } from '../../context/AuthContext';
import { useLeagues, useMiniLeagues } from '../../hooks/useQueries';
import { identityFor } from '../../data/leagues';
import { cn } from '../../lib/cn';
import { MiniLeagueExplainer } from './MiniLeagueExplainer';
import {
  createMiniLeaguePath,
  hubBackLabel,
  hubBackPath,
  type MiniLeagueNavState
} from './miniLeagueHelpers';

export default function Hub() {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state as MiniLeagueNavState | null;
  const { impact } = useHaptics();
  const { uid } = useAuth();
  const { data: miniLeagues, isLoading } = useMiniLeagues(uid);
  const { data: leagues } = useLeagues();

  const leagueName = (leagueId: string) =>
    leagues?.find((l) => l.leagueId === leagueId)?.name ?? identityFor(leagueId)?.name ?? 'League';

  const empty = !isLoading && (miniLeagues ?? []).length === 0;
  const contextLeagueId = navState?.leagueId ?? null;
  const contextLeagueName = contextLeagueId ? leagueName(contextLeagueId) : undefined;

  const goBack = () => {
    impact('light');
    navigate(hubBackPath(navState));
  };

  const goCreate = () => {
    impact('light');
    navigate(createMiniLeaguePath(contextLeagueId), { state: navState });
  };

  const goJoin = () => {
    impact('light');
    navigate('/leagues/join', { state: navState });
  };

  return (
    <AppShell>
      <Content>
        <div className="flex items-center justify-between pt-2 md:pt-5">
          <button type="button" onClick={goBack} className="tap -ml-1 flex items-center gap-[2px] text-ink">
            <ChevronLeft width={22} height={22} strokeWidth={2.4} />
            <span className="text-[16px] font-semibold tracking-snug">{hubBackLabel(navState)}</span>
          </button>
          {!empty && (
            <button
              type="button"
              aria-label="Create a mini league"
              onClick={goCreate}
              className="tap grid h-10 w-10 place-items-center rounded-full border border-black/5 bg-card shadow-soft"
            >
              <Plus width={20} height={20} strokeWidth={2.2} className="text-ink" />
            </button>
          )}
        </div>

        <div className="pt-3">
          <h1 className="title text-[30px] md:text-[36px] leading-none">Mini leagues</h1>
          <p className="mt-1.5 text-[13px] font-medium text-ink2">
            Private tables with your mates — your picks score it automatically.
          </p>
        </div>

        {empty ? (
          <div className="flex flex-1 flex-col justify-center pb-[7vh]">
            <EmptyState onCreate={goCreate} onJoin={goJoin} leagueName={contextLeagueName} />
          </div>
        ) : (
          <>
            <div className="mt-4">
              <MiniLeagueExplainer leagueName={contextLeagueName} />
            </div>

            <div className="mt-4 flex gap-[11px]">
              <button
                type="button"
                onClick={goCreate}
                className="tap flex-1 rounded-[20px] bg-gradient-to-br from-accent to-accent-deep p-[15px_14px] text-left shadow-featured"
              >
                <div className="grid h-[34px] w-[34px] place-items-center rounded-[11px] bg-white/[0.22]">
                  <Plus width={19} height={19} strokeWidth={2.6} className="text-white" />
                </div>
                <div className="mt-3.5 text-[15px] font-extrabold tracking-snug text-white">Create</div>
                <div className="mt-px text-[11.5px] font-semibold text-white/85">Start a new table</div>
              </button>
              <button
                type="button"
                onClick={goJoin}
                className="tap flex-1 rounded-[20px] bg-night p-[15px_14px] text-left shadow-dark"
              >
                <div className="grid h-[34px] w-[34px] place-items-center rounded-[11px] bg-white/10">
                  <ListOrdered width={19} height={19} strokeWidth={2.2} className="text-accent" />
                </div>
                <div className="mt-3.5 text-[15px] font-extrabold tracking-snug text-white">Join</div>
                <div className="mt-px text-[11.5px] font-semibold text-white/55">Enter a code</div>
              </button>
            </div>

            <div className="flex items-baseline justify-between pb-[10px] pt-6">
              <span className="text-[15px] font-extrabold tracking-tight text-ink">Your tables</span>
              <span className="text-[12px] font-semibold text-ink2">{(miniLeagues ?? []).length} active</span>
            </div>

            <div className="flex flex-col gap-[11px] md:grid md:grid-cols-2">
              {(miniLeagues ?? []).map((ml) => (
                <MiniLeagueCard
                  key={ml.miniLeagueId}
                  mini={ml}
                  uid={uid}
                  leagueName={leagueName(ml.leagueId)}
                  onOpen={() => {
                    impact('light');
                    navigate(`/leagues/${ml.miniLeagueId}`, { state: navState });
                  }}
                />
              ))}
            </div>
          </>
        )}
      </Content>
    </AppShell>
  );
}

function EmptyState({
  onCreate,
  onJoin,
  leagueName
}: {
  onCreate: () => void;
  onJoin: () => void;
  leagueName?: string;
}) {
  const { impact } = useHaptics();
  return (
    <div className="flex flex-col items-center text-center">
      <div className="relative h-32 w-32">
        <div className="absolute inset-0 rounded-[34px] bg-[radial-gradient(120%_100%_at_30%_0%,#16271F,#08080C)] shadow-[0_26px_50px_-22px_rgba(0,0,0,0.5)]" />
        <div className="absolute left-1/2 top-1/2 flex w-[74px] -translate-x-1/2 -translate-y-1/2 flex-col gap-[7px]">
          {[
            { dot: '#FCDE7C', bar: 'bg-white/85' },
            { dot: '#24C56A', bar: 'bg-white/50' },
            { dot: '#5B6EE1', bar: 'bg-white/30' }
          ].map((r, i) => (
            <div key={i} className="flex items-center gap-[7px]">
              <span className="h-[14px] w-[14px] rounded-full" style={{ background: r.dot }} />
              <span className={cn('h-[7px] flex-1 rounded', r.bar)} />
            </div>
          ))}
        </div>
      </div>
      <h2 className="mt-4 text-[23px] font-extrabold leading-[1.1] tracking-tight text-ink">
        Settle it with
        <br />
        your mates
      </h2>
      <p className="mt-3 max-w-[280px] text-[14px] font-medium leading-[1.45] text-ink2">
        {leagueName
          ? `Start a private ${leagueName} table. Everyone’s picks count automatically — winner takes the bragging rights.`
          : 'Start a private table with friends. Your league picks score it automatically — no extra work.'}
      </p>

      <div className="mt-6 flex w-full flex-col gap-[11px]">
        <button
          type="button"
          onClick={() => {
            impact('medium');
            onCreate();
          }}
          className="tap flex items-center justify-center gap-[9px] rounded-[17px] bg-gradient-to-br from-accent to-accent-deep py-[17px] shadow-featured"
        >
          <Plus width={19} height={19} strokeWidth={2.6} className="text-white" />
          <span className="text-[16px] font-extrabold tracking-snug text-white">Create a mini league</span>
        </button>
        <button
          type="button"
          onClick={() => {
            impact('light');
            onJoin();
          }}
          className="tap flex items-center justify-center gap-[9px] rounded-[17px] bg-fill py-4"
        >
          <ListOrdered width={18} height={18} strokeWidth={2.2} className="text-ink" />
          <span className="text-[15px] font-bold tracking-snug text-ink">Join with a code</span>
        </button>
      </div>
    </div>
  );
}
